/**
 * Target Number Line Component
 *
 * Visualizes multiple data points (current, proposed, target, min, max) on a horizontal line.
 *
 * - Values: Numeric data extracted from inputs (which can be either numeric values or ratings)
 *   Values are sorted and processed to determine relationships between them
 *
 * - Boundaries: Min/max range that defines the scale of the visualization
 *   Calculated from the smallest and largest provided values
 *   Used to normalize all positions onto a 0-100% scale
 *
 * - Positions: Where values appear on the line (as percentages 0-100%)
 *   Calculated proportionally based on value's position within boundaries
 *   Includes minimum spacing logic to prevent overlapping
 *   Grouped into top (current, proposed) and bottom (target, min, max) placements
 *   When current value is out-of-bounds, it's visually flagged and connected to relevant point
 *
 * @example
 * // Basic usage with current and target values
 * <TargetNumberLine
 *   current={{ value: 40, label: '40%' }}
 *   target={{ value: 50, label: '50%' }}
 * />
 *
 * @example
 * // Full configuration with all possible values
 * <TargetNumberLine
 *   current={{ value: 30, label: '30%' }}
 *   proposed={{ value: 45, label: '45%' }}
 *   target={{ value: 50, label: '50%' }}
 *   min={{ value: 20, label: '20%' }}
 *   max={{ value: 70, label: '70%' }}
 * />
 *
 * @example
 * // Using S&P rating values instead of numeric values
 * <TargetNumberLine
 *   current={{ rating: SPRating.C }}
 *   target={{ rating: SPRating.A }}
 *   min={{ rating: SPRating.D }}
 *   max={{ rating: SPRating.AAA }}
 * />
 *
 * @example
 * // Using Moody's rating values
 * <TargetNumberLine
 *   current={{ rating: MoodysRating.Baa3 }}
 *   target={{ rating: MoodysRating.A2 }}
 *   min={{ rating: MoodysRating.Ba1 }}
 *   max={{ rating: MoodysRating.Aaa }}
 * />
 *
 * @example
 * // Using years as the unit
 * <TargetNumberLine
 *   current={{ value: 4.5, unit: ValueUnit.Years }}
 *   target={{ value: 3.2, unit: ValueUnit.Years }}
 *   min={{ value: 1.0, unit: ValueUnit.Years }}
 *   max={{ value: 10.0, unit: ValueUnit.Years }}
 * />
 *
 * @example
 * // Using dollars as the unit
 * <TargetNumberLine
 *   unit={ValueUnit.Dollars}
 *   current={{ value: 125000 }}
 *   target={{ value: 150000 }}
 *   min={{ value: 100000 }}
 *   max={{ value: 200000 }}
 * />
 */

'use client'

import type {
  CalculatedValues,
  NumericValue,
  RatingValue,
  TargetLineCalculatorProps,
  TargetNumberLineProps,
} from './types'

import { useCallback, useMemo } from 'react'

import { ValueUnit } from '@/types/enums/value-unit'

import {
  CURRENT_INDEX,
  calculateBoundaries,
  calculateRelativePosition,
  checkValueBounds,
  determineConnectionPosition,
  extractAndSortValues,
  extractValue,
  getDisplayValue,
  getValueUnit,
  MAX_INDEX,
  MIN_INDEX,
  PROPOSED_INDEX,
  TARGET_INDEX,
} from './helpers'
import {
  Container,
  DottedLine,
  ScoreIndicator,
  ScoreLabel,
  ScoreLine,
  ScoreLineContainer,
  Track,
} from './styles'

function useTargetLineCalculator({
  current,
  proposed,
  target,
  min,
  max,
  unit = ValueUnit.Percentage,
}: TargetLineCalculatorProps): CalculatedValues {
  // Determine the active unit - take it from one of the values (preference order) or use default
  const activeUnit = useMemo(() => {
    if (current && getValueUnit(current, unit) !== ValueUnit.Percentage) {
      return getValueUnit(current, unit)
    }
    if (target && getValueUnit(target, unit) !== ValueUnit.Percentage) {
      return getValueUnit(target, unit)
    }
    if (proposed && getValueUnit(proposed, unit) !== ValueUnit.Percentage) {
      return getValueUnit(proposed, unit)
    }
    if (min && getValueUnit(min, unit) !== ValueUnit.Percentage) {
      return getValueUnit(min, unit)
    }
    if (max && getValueUnit(max, unit) !== ValueUnit.Percentage) {
      return getValueUnit(max, unit)
    }
    return unit
  }, [current, target, proposed, min, max, unit])

  // Extract all values
  const extractedValues = useMemo(
    () => ({
      current: extractValue(current),
      proposed: extractValue(proposed),
      target: extractValue(target),
      min: extractValue(min),
      max: extractValue(max),
    }),
    [current, proposed, target, min, max],
  )

  // Check if proposed value is the same as current value
  const isProposedSameAsCurrent = useMemo(() => {
    return (
      extractedValues.current !== null &&
      extractedValues.proposed !== null &&
      extractedValues.current === extractedValues.proposed
    )
  }, [extractedValues])

  // Check if target value is the same as min or max value
  const isTargetSameAsMinOrMax = useMemo(() => {
    const targetExists = extractedValues.target !== null
    const rangeHasWidth = extractedValues.min !== extractedValues.max
    const targetIsAtMinBoundary =
      extractedValues.min !== null &&
      extractedValues.target === extractedValues.min
    const targetIsAtMaxBoundary =
      extractedValues.max !== null &&
      extractedValues.target === extractedValues.max

    return (
      targetExists &&
      rangeHasWidth &&
      (targetIsAtMinBoundary || targetIsAtMaxBoundary)
    )
  }, [extractedValues])

  // Sort values from smallest to largest
  const sortedValues = useMemo(
    () => extractAndSortValues(extractedValues),
    [extractedValues],
  )

  // Calculate min/max boundaries from the values
  const boundaries = useMemo(
    () => calculateBoundaries(sortedValues),
    [sortedValues],
  )

  // Check if all non-null values are identical
  const allSameValue = useMemo(() => {
    const nonNullValues = Object.values(extractedValues).filter(
      (v) => v !== null,
    )
    return (
      nonNullValues.length > 1 &&
      nonNullValues.every((v) => v === nonNullValues[0])
    )
  }, [extractedValues])

  const positions = useMemo(() => {
    // Extract values in correct order with undefined fallback to null
    const values = [
      extractedValues.min ?? null, // MIN_INDEX
      extractedValues.current ?? null, // CURRENT_INDEX
      extractedValues.proposed ?? null, // PROPOSED_INDEX
      extractedValues.target ?? null, // TARGET_INDEX
      extractedValues.max ?? null, // MAX_INDEX
    ]

    // Calculate positions with minimum spacing
    const calculatedPositions = calculateRelativePosition(values, boundaries)

    // Map results back to named positions
    return {
      // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
      min: calculatedPositions[MIN_INDEX]!,
      // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
      current: calculatedPositions[CURRENT_INDEX]!,
      // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
      proposed: calculatedPositions[PROPOSED_INDEX]!,
      // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
      target: calculatedPositions[TARGET_INDEX]!,
      // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
      max: calculatedPositions[MAX_INDEX]!,
    }
  }, [extractedValues, boundaries])

  // Check if 'current' is out of bounds
  const { isCurrentBelowMin, isCurrentAboveMax, isCurrentOutOfBounds } =
    useMemo(
      () => checkValueBounds(extractedValues, allSameValue),
      [extractedValues, allSameValue],
    )

  // Determine which position to connect with dotted line
  const connectToPosition = useMemo(
    () =>
      determineConnectionPosition(
        extractedValues.current,
        isCurrentBelowMin,
        isCurrentAboveMax,
        positions,
      ),
    [extractedValues, isCurrentBelowMin, isCurrentAboveMax, positions],
  )

  // Create a callback that passes the active unit to getDisplayValue
  const getDisplayValueCallback = useCallback(
    (value: RatingValue | NumericValue) => {
      return getDisplayValue(value, activeUnit)
    },
    [activeUnit],
  )

  return {
    minValue: boundaries.min,
    maxValue: boundaries.max,
    currentPosition: positions.current,
    proposedPosition: positions.proposed,
    targetPosition: positions.target,
    minPosition: positions.min,
    maxPosition: positions.max,
    isCurrentOutOfBounds,
    isCurrentBelowMin,
    isCurrentAboveMax,
    connectToPosition,
    getDisplayValue: getDisplayValueCallback,
    allSameValue,
    isProposedSameAsCurrent,
    isTargetSameAsMinOrMax,
    unit: activeUnit,
  }
}

export function TargetNumberLine({
  current,
  proposed,
  target,
  min,
  max,
  className,
  unit = ValueUnit.Percentage,
}: TargetNumberLineProps) {
  const {
    currentPosition,
    proposedPosition,
    targetPosition,
    minPosition,
    maxPosition,
    isCurrentOutOfBounds,
    connectToPosition,
    getDisplayValue,
    allSameValue,
    isProposedSameAsCurrent,
    isTargetSameAsMinOrMax,
  } = useTargetLineCalculator({
    current,
    proposed,
    target,
    min,
    max,
    unit,
  })

  const hasNoValues =
    (current === undefined || current === null) &&
    (proposed === undefined || proposed === null) &&
    (target === undefined || target === null) &&
    (min === undefined || min === null) &&
    (max === undefined || max === null)

  if (hasNoValues) return null

  return (
    <Container className={className}>
      {/* Background line */}
      <Track />

      <ScoreLineContainer>
        {/* Current value label */}
        {current && (
          <ScoreLabel
            // biome-ignore lint/style/noNonNullAssertion: Bulk suppression as part of biome migration
            position={currentPosition!}
            label="Current"
            value={getDisplayValue(current)}
            variant="current"
            warning={isCurrentOutOfBounds}
            placement="top"
          />
        )}

        {/* Proposed value label at the top */}
        {proposed && (
          <ScoreLabel
            // biome-ignore lint/style/noNonNullAssertion: Bulk suppression as part of biome migration
            position={proposedPosition!}
            label="Proposed"
            value={getDisplayValue(proposed)}
            variant="proposal"
            placement="top"
            className={isProposedSameAsCurrent ? '-mt-3.75' : ''}
          />
        )}

        <ScoreLine>
          {/* Dotted line connecting 'current' to the appropriate value - hidden when all values are the same */}
          {!allSameValue &&
            current &&
            currentPosition !== null &&
            connectToPosition !== null && (
              <DottedLine
                currentPosition={currentPosition}
                targetPosition={connectToPosition}
                isWarning={isCurrentOutOfBounds}
              />
            )}

          {/* Current value indicator */}
          {current && (
            <ScoreIndicator
              // biome-ignore lint/style/noNonNullAssertion: Bulk suppression as part of biome migration
              position={currentPosition!}
              aria-label={`Current: ${getDisplayValue(current)}`}
              warning={isCurrentOutOfBounds}
              variant="current"
            />
          )}

          {/* Proposed value indicator */}
          {proposed && (
            <ScoreIndicator
              // biome-ignore lint/style/noNonNullAssertion: Bulk suppression as part of biome migration
              position={proposedPosition!}
              aria-label={`Proposed: ${getDisplayValue(proposed)}`}
              variant="proposal"
            />
          )}

          {/* Target value indicator */}
          {target && (
            <ScoreIndicator
              // biome-ignore lint/style/noNonNullAssertion: Bulk suppression as part of biome migration
              position={targetPosition!}
              variant="target"
              aria-label={`Target: ${getDisplayValue(target)}`}
            />
          )}

          {/* Minimum value indicator */}
          {min && (
            <ScoreIndicator
              // biome-ignore lint/style/noNonNullAssertion: Bulk suppression as part of biome migration
              position={minPosition!}
              aria-label={`Min: ${getDisplayValue(min)}`}
              variant="default"
            />
          )}

          {/* Maximum value indicator */}
          {max && (
            <ScoreIndicator
              // biome-ignore lint/style/noNonNullAssertion: Bulk suppression as part of biome migration
              position={maxPosition!}
              aria-label={`Max: ${getDisplayValue(max)}`}
              variant="default"
            />
          )}
        </ScoreLine>

        {/* Target value label at the bottom */}
        {target && (
          <ScoreLabel
            // biome-ignore lint/style/noNonNullAssertion: Bulk suppression as part of biome migration
            position={targetPosition!}
            label="Target"
            value={getDisplayValue(target)}
            variant="target"
            className={isTargetSameAsMinOrMax ? '-bottom-3.75 z-0' : ''}
            // placement="bottom" is the default
          />
        )}

        {/* Minimum value label */}
        {min && (
          <ScoreLabel
            // biome-ignore lint/style/noNonNullAssertion: Bulk suppression as part of biome migration
            position={minPosition!}
            label="Min"
            value={getDisplayValue(min)}
          />
        )}

        {/* Maximum value label */}
        {max && (
          <ScoreLabel
            // biome-ignore lint/style/noNonNullAssertion: Bulk suppression as part of biome migration
            position={maxPosition!}
            label="Max"
            value={getDisplayValue(max)}
          />
        )}
      </ScoreLineContainer>
    </Container>
  )
}
